import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faExclamationTriangle,
  faExternalLinkAlt,
  faRotateRight,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

const isTableSeparator = (line) => /^\s*\|?[\s:|-]+\|[\s:|-]+\|?\s*$/.test(line);
const isTableRow = (line) => line.includes("|");
const isListLine = (line) => /^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line);
const isHeadingLine = (line) => /^#{1,6}\s+/.test(line);

const splitTableRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const renderInline = (text) => {
  const fragments = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      fragments.push(text.slice(lastIndex, match.index));
    }

    const token = match[0];
    if (token.startsWith("`")) {
      fragments.push(
        <code key={`code-${match.index}`} className="px-1 py-0.5 bg-gray-100 border rounded text-[12px] text-gray-900">
          {token.slice(1, -1)}
        </code>
      );
    } else {
      fragments.push(
        <strong key={`strong-${match.index}`} className="font-semibold text-gray-900">
          {token.slice(2, -2)}
        </strong>
      );
    }

    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) {
    fragments.push(text.slice(lastIndex));
  }

  return fragments;
};

const renderMarkdownBlocks = (markdown) => {
  const lines = String(markdown || "").split(/\r?\n/);
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (line.trim().startsWith("```")) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !lines[index].trim().startsWith("```")) {
        codeLines.push(lines[index]);
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      blocks.push(
        <pre key={`code-${blocks.length}`} className="my-3 overflow-x-auto rounded-lg bg-gray-900 p-3 text-xs leading-5 text-white">
          <code>{codeLines.join("\n")}</code>
        </pre>
      );
      continue;
    }

    if (isHeadingLine(line)) {
      const level = Math.min((line.match(/^#+/)?.[0] || "#").length, 3);
      const text = line.replace(/^#{1,6}\s+/, "").trim();
      const className =
        level === 1
          ? "mt-1 mb-3 text-xl font-bold text-gray-900"
          : level === 2
            ? "mt-5 mb-2 text-base font-bold text-gray-900"
            : "mt-4 mb-2 text-sm font-bold text-gray-900";
      const HeadingTag = `h${level}`;

      blocks.push(
        <HeadingTag key={`heading-${blocks.length}`} className={className}>
          {renderInline(text)}
        </HeadingTag>
      );
      index += 1;
      continue;
    }

    if (isTableRow(line) && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const header = splitTableRow(line);
      const rows = [];
      index += 2;

      while (index < lines.length && isTableRow(lines[index]) && lines[index].trim()) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }

      blocks.push(
        <div key={`table-${blocks.length}`} className="my-3 overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {header.map((cell, cellIndex) => (
                  <th key={cellIndex} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-3 py-2 align-top text-gray-700">
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^\s*\d+\.\s+/.test(line)) {
      const startNumber = parseInt(line.match(/^\s*(\d+)\./)?.[1] || "1", 10);
      const items = [];
      while (index < lines.length && /^\s*\d+\.\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*\d+\.\s+/, "").trim());
        index += 1;
      }
      blocks.push(
        <ol key={`ol-${blocks.length}`} start={startNumber} className="my-2 list-decimal space-y-1 pl-6 text-sm leading-6 text-gray-700">
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, "").trim());
        index += 1;
      }
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="my-2 list-disc space-y-1 pl-6 text-sm leading-6 text-gray-700">
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{renderInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    const paragraphLines = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isHeadingLine(lines[index]) &&
      !isListLine(lines[index]) &&
      !lines[index].trim().startsWith("```")
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    blocks.push(
      <p key={`p-${blocks.length}`} className="my-2 text-sm leading-6 text-gray-700">
        {renderInline(paragraphLines.join(" "))}
      </p>
    );
  }

  return blocks;
};

export const MarkdownSpecsContent = ({ content, className = "mx-auto max-w-4xl pb-3" }) => {
  const renderedContent = useMemo(() => renderMarkdownBlocks(content), [content]);

  return <article className={className}>{renderedContent}</article>;
};

const BatchFileSpecsModal = ({
  endpoint,
  isOpen,
  onClose,
  title,
  fullPagePath,
  fullPageLabel = "View full page",
}) => {
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const loadSpecs = () => {
    if (!endpoint) return;

    setLoading(true);
    setError("");

    axios
      .get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          Accept: "text/markdown,text/plain,*/*",
        },
        responseType: "text",
        transformResponse: [(data) => data],
      })
      .then((response) => {
        setContent(typeof response.data === "string" ? response.data : "");
      })
      .catch((requestError) => {
        const responseText = requestError?.response?.data;
        setError(typeof responseText === "string" && responseText.trim() ? responseText : "Unable to load file specs.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      loadSpecs();
    }
  }, [isOpen, endpoint]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b bg-gray-50 px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
              <FontAwesomeIcon icon={faBookOpen} />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-gray-900">{title || "File Specs"}</h2>
              <p className="text-xs text-gray-500">Loaded from the markdown specs file.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            title="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 text-gray-600">
              <FontAwesomeIcon icon={faSpinner} className="text-2xl text-indigo-600 animate-spin" />
              <p className="text-sm font-medium">Loading file specs...</p>
            </div>
          ) : error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mt-1 text-red-600" />
                <div className="flex-1">
                  <p className="text-sm font-semibold">Specs unavailable</p>
                  <p className="mt-1 text-sm">{error}</p>
                  <button
                    type="button"
                    onClick={loadSpecs}
                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
                  >
                    <FontAwesomeIcon icon={faRotateRight} />
                    Retry
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <MarkdownSpecsContent content={content} />
          )}
        </div>

        <div className="flex flex-col gap-2 border-t bg-gray-50 px-5 py-3 sm:flex-row sm:justify-end">
          {fullPagePath && (
            <a
              href={fullPagePath}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              {fullPageLabel}
            </a>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchFileSpecsModal;
